'use client';

import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'next/navigation';
import { Locale } from '@/project.config';
import useGA4WidgetImpressions from '@/hooks/useGA4WidgetImpressions';
import { pushSelectItem } from '@/lib/analytics/dataLayer';
import { mappedProductToGA4Item } from '@/lib/analytics/ga4Items';
import { useTranslations } from 'use-intl';
import { ProductTileV2Role } from '@/components/molecules/product-tile-v2/types';
import AddToPurchaseListDrawer from '@/components/organisms/add-to-purchase-list-drawer';
import FeaturedProductsCarousel from '@/components/organisms/featured-products-carousel';
import { FeaturedProductEntry } from '@/components/organisms/featured-products-carousel/types';
import toast from '@/components/atoms/toaster/helpers/toast';
import useAccount from '@/lib/hooks/useAccount';
import useAccountRoles from '@/lib/hooks/useAccountRoles';
import useShoppingListAccess from '@/lib/hooks/useShoppingListAccess';
import useCart from '@/lib/hooks/useCart';
import usePurchaseLists from '@/lib/hooks/usePurchaseLists';
import { addToPurchaseListWithFeedback } from '@/lib/tastics/product-list/components/product-list-view-model/helpers/addToPurchaseListWithFeedback';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import { DataSource } from '@/types/lib/datasources';
import { applyCtSnapshot } from '@/utils/mappers/applyCtSnapshot';
import { mapProduct } from '@/utils/mappers/map-product';
import { mapPurchaseList } from '@/utils/mappers/map-purchase-list';
import { normalizeBrandName } from '@/utils/mappers/normalize-brand-name';
import { Product } from '@shared/types/product';
import { Props } from '../../types';
import type { CtProductsBySku } from '@/utils/server/getCtSnapshotForSku';

interface FeaturedCarouselClientProps {
  data: DataSource<{ items: Product[] }> & Props;
  /**
   * EWM-636 — authoritative Standalone price + stock per SKU, resolved + cached
   * server-side (the whole section in one entry). Merged onto each mapped product
   * so the carousel shows the real price (not the embedded `$0`). Empty when the
   * resolve failed or no pricing scope — tiles then render "Unavailable".
   */
  ctBySku: CtProductsBySku;
}

// Brand attribute, re-keyed by the BFF as `variants.attributes.<name>` (same
// access pattern as map-product / map-purchase-list). EWM-327 — the value may
// be a plain string, a JSON envelope `{"brandInfo":{"name":...}}` or empty;
// `normalizeBrandName` resolves every shape to the display name.
const brandOf = (product: Product): string | undefined => {
  const attributes = (product.variants?.[0]?.attributes ?? {}) as Record<string, unknown>;
  return normalizeBrandName(attributes['variants.attributes.productBrand']);
};

const FeaturedCarouselClientWrapper = ({ data, ctBySku }: FeaturedCarouselClientProps) => {
  const translate = useTranslations();

  const { selectedBusinessUnit, selectedStore } = useStoreAndBusinessUnits();
  const { account } = useAccount();
  const { permissions } = useAccountRoles(selectedBusinessUnit?.key);
  const { canUsePurchaseLists } = useShoppingListAccess(selectedBusinessUnit?.key);
  const { addItem, cart } = useCart(selectedBusinessUnit?.key, selectedStore?.key);
  const { purchaseLists: rawWishlists, addToWishlists, createPurchaseList } = usePurchaseLists(selectedStore?.key);

  const [addingEntry, setAddingEntry] = useState<FeaturedProductEntry | null>(null);

  const role: ProductTileV2Role = !account ? 'guest' : permissions.UpdateMyCarts ? 'master' : 'view-only';

  // EWM-643 — real locale for the per-country removed-from-sale (`GO`) flag.
  const { locale } = useParams<{ locale: Locale }>();

  // The data source preserves the authored SKU order; cap to `maxItems` (same cap
  // the server entry used when resolving prices). EWM-636 — merge the authoritative
  // Standalone price + stock from `ctBySku` onto each mapped product so the card
  // shows the real price (not the embedded `$0`); `priceUnavailable` /
  // `stockUnavailable` gate the tile's "Unavailable" surfaces.
  const entries: FeaturedProductEntry[] = useMemo(() => {
    // EWM-760 — `data` is a required prop (already read non-optionally below via
    // `data.maxItems`), so the leading `?.` on `data` here was redundant; dropping
    // it lets the compiler's inferred dependency match the source dependency
    // (both `data.data?.dataSource?.items`) with no behaviour change.
    const items = data.data?.dataSource?.items ?? [];
    return items.slice(0, data.maxItems ?? 12).map((product) => {
      const sku = product.variants?.[0]?.sku ?? '';
      const { item, priceUnavailable, stockUnavailable } = applyCtSnapshot(
        mapProduct(product, { cart, locale }),
        ctBySku[sku],
      );
      return { item, brand: brandOf(product), priceUnavailable, stockUnavailable };
    });
  }, [data.data?.dataSource?.items, data.maxItems, cart, ctBySku, locale]);

  const listId = 'widget_featured-carousel';
  const listName = data.heading || 'Featured Products';

  const ga4Items = useMemo(
    () => entries.map((entry, i) => mappedProductToGA4Item(entry.item, i + 1, listId, listName)),
    [entries, listName],
  );

  const { containerRef } = useGA4WidgetImpressions({ items: ga4Items, listId, listName });

  const lists = useMemo(
    () =>
      (rawWishlists?.items ?? []).map((wishlist) => {
        const mapped = mapPurchaseList(wishlist);
        return { id: mapped.id, name: mapped.name, itemCount: mapped.items.length };
      }),
    [rawWishlists],
  );

  const handleAddToCart = useCallback(
    async (sku: string, qty: number) => {
      const res = await addItem([{ sku, count: qty }]);
      if (!res.success) {
        const message = res.message || translate('common.something-went-wrong');
        toast.error(message);
        return;
      }
      // EWM-434 — toast on success instead of the (removed) mini-cart drawer.
      toast.success(translate('cart.item-added'), { position: 'top-right' });
    },
    [addItem, translate],
  );

  const handleSubmitPurchaseList = useCallback(
    async (selectedListIds: string[]) => {
      if (!addingEntry) return;
      await addToPurchaseListWithFeedback({
        addToWishlists,
        translate,
        sku: addingEntry.item.sku ?? '',
        wishlistIds: selectedListIds,
      });
      setAddingEntry(null);
    },
    [addingEntry, addToWishlists, translate],
  );

  // EWM-601 — create a purchase list inline from the drawer; return the created
  // list (drawer shape) so it shows + auto-selects. New lists start empty.
  const handleCreateList = useCallback(
    async (name: string) => {
      const created = await createPurchaseList({ name, store: selectedStore });
      return created?.wishlistId ? { id: created.wishlistId, name: created.name ?? name, itemCount: 0 } : null;
    },
    [createPurchaseList, selectedStore],
  );

  // Data source not resolved (e.g. BFF 5xx) → render nothing; the rest of the
  // page is unaffected.
  if (!data?.data?.dataSource) return null;

  return (
    <>
      <div ref={containerRef as React.RefCallback<HTMLDivElement>}>
        <FeaturedProductsCarousel
          eyebrow={data.eyebrow}
          heading={data.heading}
          intro={data.intro}
          role={role}
          canAddToPurchaseList={canUsePurchaseLists}
          backOrdersAllowed={selectedBusinessUnit?.backOrdersAllowed ?? false}
          products={entries}
          showActions={data.showAddToCart ?? true}
          showRrp={data.showRrp ?? true}
          autoplay={data.autoplay ?? false}
          onAddToCart={handleAddToCart}
          onAddToPurchaseList={setAddingEntry}
          onProductClick={(sku, index) => {
            const entry = entries.find((e) => e.item.sku === sku);
            if (!entry) return;
            pushSelectItem({
              item_list_id: listId,
              item_list_name: listName,
              item: mappedProductToGA4Item(entry.item, index, listId, listName),
            });
          }}
        />
      </div>

      {addingEntry && (
        <AddToPurchaseListDrawer
          isOpen={!!addingEntry}
          product={{ name: addingEntry.item.name, sku: addingEntry.item.sku ?? '' }}
          lists={lists}
          onCreateList={handleCreateList}
          onSubmit={handleSubmitPurchaseList}
          onClose={() => setAddingEntry(null)}
        />
      )}
    </>
  );
};

export default FeaturedCarouselClientWrapper;
